import { ref, computed } from 'vue';
import { Capacitor } from '@capacitor/core';
import { useAppConfig } from '@/core/useAppConfig.js';
import { TIER_ORDER, hasFeature } from '@/config/tiers.js';
import { subscriptionApi } from '@/api/index.js';

// Random UUID is correct for anonymous/pre-login devices; useAuth's verifyCode() overwrites it
// with the server-assigned tenant_id after login (AuthGate.js:170). Gap: on a brand-new device
// the pre-login UUID may not match what the server resolves, causing a one-frame mismatch until
// verifyCode() lands — a device-registration flow would close this.
function getOrCreateTenantId(): string {
  const key = 'punchlog_tenant_id';
  let id = localStorage.getItem(key);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(key, id);
  }
  return id;
}

// Native builds open Stripe Checkout/Portal in the system browser (openUrl below), which can't
// land back on a hash route (createWebHashHistory) or hold onto in-memory app state -- it needs a
// registered custom URL scheme to hand control back to the app. Web keeps the existing hash URL.
// The `punchlog://` scheme is registered in AndroidManifest.xml and ios/App/App/Info.plist; the
// appUrlOpen listener that routes it back to Settings > Plan lives in main.ts.
function planUrl(query?: string): string {
  const suffix = query ? `?${query}` : '';
  if (Capacitor.isNativePlatform()) return `punchlog://settings/plan${suffix}`;
  const origin = globalThis.location?.origin ?? 'https://pl.ddproxy.com';
  return `${origin}/#/settings/plan${suffix}`;
}

// ponytail: window.open fallback; swap for @capacitor/browser when native deep-links are wired
function openUrl(url: string) {
  if (globalThis.window !== undefined) {
    globalThis.window.open(url, '_blank', 'noopener');
  }
}

async function createSessionAndOpen(sessionCall: () => Promise<{ url: string }>, errMsg: string) {
  let url: string;
  try {
    ({ url } = await sessionCall());
  } catch (e: any) {
    throw new Error(e.message ?? errMsg);
  }
  openUrl(url);
}

function restorePersistedTier(config: any) {
  if (_synced || !config.value.subscriptionTier) return;
  tier.value = config.value.subscriptionTier;
  status.value = config.value.subscriptionStatus ?? 'free';
  lastSyncedAt.value = config.value.subscriptionSyncedAt ?? null;
}

function applySyncedTierData(config: any, data: any, lastSyncedAt: number) {
  tier.value = data.tier;
  status.value = data.status;
  seatCount.value = data.seatCount;
  invoiceLimit.value = data.invoiceLimit;
  invoiceUsed.value = data.invoiceUsed;
  cancelAtPeriodEnd.value = data.cancelAtPeriodEnd;
  currentPeriodEnd.value = data.currentPeriodEnd;
  config.value.subscriptionTier = data.tier;
  config.value.subscriptionStatus = data.status;
  config.value.subscriptionSyncedAt = lastSyncedAt;
}

// Free-tier accounts never change server-side on their own — polling tenant/status on every page
// load buys nothing for the ~99% of users who stay free, and the same "read the cache, only hit
// the network when it can plausibly be stale" logic applies to paid accounts between checkouts.
const SYNC_CACHE_MS = 30 * 60 * 1000;

const tier = ref<string>('free');
const status = ref<string>('free');
const seatCount = ref<number | null>(null);
const invoiceLimit = ref<number>(5);
const invoiceUsed = ref<number>(0);
const cancelAtPeriodEnd = ref<boolean>(false);
const currentPeriodEnd = ref<string | null>(null);
const syncing = ref(false);
const lastSyncedAt = ref<number | null>(null);

let _synced = false;

export function useSubscription() {
  const { config } = useAppConfig();

  restorePersistedTier(config);

  // opts.force bypasses both the free-tier skip and the 30-minute cache — use it for events that
  // genuinely need a fresh read (post-checkout return, an explicit user-triggered refresh button),
  // never for routine page-load calls.
  async function syncTier(opts: { force?: boolean } = {}) {
    if (syncing.value) return;
    if (!opts.force) {
      const cacheFresh =
        lastSyncedAt.value !== null && Date.now() - lastSyncedAt.value < SYNC_CACHE_MS;
      if (cacheFresh) return;
    }
    syncing.value = true;
    try {
      const tenantId = getOrCreateTenantId();
      const data = await subscriptionApi.status(tenantId);
      lastSyncedAt.value = Date.now();
      applySyncedTierData(config, data, lastSyncedAt.value);
      _synced = true;
    } catch {
      // offline — use cached value
    } finally {
      syncing.value = false;
    }
  }

  // seats only applies to the biz tier (per-seat pricing); ignored server-side for basic/pro.
  function startCheckout(targetTier: string, seats?: number) {
    const tenantId = getOrCreateTenantId();
    return createSessionAndOpen(
      () =>
        subscriptionApi.createCheckoutSession({
          tenant_id: tenantId,
          tier: targetTier,
          ...(seats === undefined ? {} : { seats }),
          cancel_url: planUrl(),
          success_url: planUrl('checkout=success'),
        }),
      'Checkout failed',
    );
  }

  function openPortal() {
    const tenantId = getOrCreateTenantId();
    return createSessionAndOpen(
      () =>
        subscriptionApi.createBillingPortalSession({
          return_url: planUrl(),
          tenant_id: tenantId,
        }),
      'Portal failed',
    );
  }

  const tierRank = computed(() => TIER_ORDER.indexOf(tier.value));
  const isPaid = computed(() => tier.value !== 'free');

  return {
    cancelAtPeriodEnd,
    currentPeriodEnd,
    hasFeature: (id: string) => hasFeature(tier.value, id),
    invoiceLimit,
    invoiceUsed,
    isPaid,
    lastSyncedAt,
    openPortal,
    seatCount,
    startCheckout,
    status,
    syncing,
    syncTier,
    tenantId: getOrCreateTenantId,
    tier,
    tierRank,
  };
}
